import { useSubsidiaries } from '~/features/organization/hooks/use-subsidiaries';
import { useAuthStore } from '~/store/auth.store';

interface SubsidiaryNameProps {
  id: string | null | undefined;
  /** Name the backend sent with the estimate; wins when present. */
  name?: string | null | undefined;
  className?: string;
  /** `data-testid` on the rendered name (default `subsidiary-name`). */
  testId?: string;
}

/**
 * Subsidiary display name: the estimate's own `subsidiaryName`, else the viewer's accessible
 * tenants (no request), else the subsidiary list lookup, else the id. The list is only read
 * when the first two cannot name it.
 */
export function SubsidiaryName({
  id,
  name,
  className,
  testId = 'subsidiary-name',
}: SubsidiaryNameProps) {
  const tenantName = useAuthStore(
    (s) => s.user?.accessibleTenants.find((tenant) => tenant.id === id)?.name,
  );
  if (name ?? tenantName)
    return (
      <span data-testid={testId} className={className}>
        {name ?? tenantName}
      </span>
    );
  if (!id)
    return (
      <span data-testid={testId} className={className}>
        —
      </span>
    );
  return <SubsidiaryLookup id={id} testId={testId} {...(className ? { className } : {})} />;
}

/** Fallback: the subsidiary list (cached query), then the raw id in mono. */
function SubsidiaryLookup({
  id,
  className,
  testId,
}: {
  id: string;
  className?: string;
  testId: string;
}) {
  const { data } = useSubsidiaries();
  const found = data?.find((s) => s.id === id)?.name;
  if (found)
    return (
      <span data-testid={testId} className={className}>
        {found}
      </span>
    );
  return (
    <span data-testid={testId} className={className} title={id}>
      <span className="font-mono text-xs">{id}</span>
    </span>
  );
}
